import { PlusCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Command,
  CommandInput,
  CommandList,
  CommandSeparator,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { Separator } from '@/components/ui/separator.js';
import { cn } from '@/lib/tailwind-utils.js';

interface FilterDropdownBaseProps {
  title?: string;
  totalSelected: number;
  selectedLabels: string[];
  isLoading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  shouldFilter?: boolean;
  onClearFilters?: () => void;
  onOpenChange?: (open: boolean) => void;
  onInteractOutside?: (e: Event) => void;
  onFocusOutside?: (e: Event) => void;
  headerSlot?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function FilterDropdownBase({
  title,
  totalSelected,
  selectedLabels,
  isLoading = false,
  search,
  onSearchChange,
  shouldFilter = true,
  onClearFilters,
  onOpenChange,
  onInteractOutside,
  onFocusOutside,
  headerSlot,
  children,
  className,
  'data-testid': dataTestId,
}: FilterDropdownBaseProps) {
  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed" loading={isLoading} data-testid={dataTestId}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {title}
          {totalSelected > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {totalSelected}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {totalSelected > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {totalSelected} selected
                  </Badge>
                ) : (
                  selectedLabels.map((label) => (
                    <Badge key={label} variant="secondary" className="rounded-sm px-1 font-normal">
                      {label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[200px] p-0', className)}
        align="start"
        onInteractOutside={onInteractOutside}
        onFocusOutside={onFocusOutside}
      >
        <Command shouldFilter={shouldFilter}>
          <CommandInput placeholder={title} value={search} onValueChange={onSearchChange} />
          {headerSlot}
          <CommandList>
            {children}
            {onClearFilters && totalSelected > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={onClearFilters} className="justify-center text-center">
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
